// Retkipassi: taso, rastikortti, kokoelmat, pistehistoria ja asetukset
// (suunnitelman kohdat 9, 11.3, 12.3 ja 20.2).

import { useEffect, useState } from "preact/hooks";

import { getServerSettings, setServerSettings } from "../lib/api.js";
import { clearLog, formatLog, readLog } from "../lib/diagnostics.js";
import { saveTextFile } from "../lib/files.js";
import { getPrefs, setPrefs } from "../lib/prefs.js";
import { Sheet } from "./Sheet.jsx";
import { AccountProfile } from "./AccountViews.jsx";
import { TeamCard, TeamAdmin } from "./TeamViews.jsx";
import { UpdateCard } from "./UpdateSheet.jsx";
import { BackupStatusCard, BackupWarning } from "./BackupStatus.jsx";
import { ContentAdminCard } from "./ContentAdmin.jsx";
import { BigTilesCard } from "./BigTilesCard.jsx";
import { ShapesCard } from "./ShapesCard.jsx";
import { SKINS, skinById, themeApplies } from "../lib/skins.js";
import { day, kmValue, points as fmtPoints, todayIso } from "../lib/format.js";

/**
 * Avattava osio: otsikko ja määrä näkyvät aina, sisältö vasta avattuna. Pitkä Passi pysyy selattavana pienellä näytöllä,
 * ja harvoin tarvittavat osiot (asetukset, viennit, ylläpito) eivät peitä edistymistä.
 */
export function Fold({ title, count, open = false, children }) {
  return (
    <details class="fold" open={open}>
      <summary><h2>{title}{count != null && <span class="count"> {count}</span>}</h2></summary>
      <div class="fold-body">{children}</div>
    </details>
  );
}

export function PassView({ app }) {
  const { stats, content } = app;
  const [ledger, setLedger] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [badges, setBadges] = useState([]);
  const [openBadge, setOpenBadge] = useState(null);
  useEffect(() => {
    app.api.ledger().then(setLedger, () => setLedger(null));
    app.api.achievements().then(setBadges, () => setBadges([]));
  }, [app.dataVersion]);

  const lv = stats ?? { level: 1, xp: 0, level_start: 0, next_level_at: 100 };
  const progress = Math.min(1, (lv.xp - lv.level_start) / Math.max(1, lv.next_level_at - lv.level_start));
  const shown = ledger ? (showAll ? ledger : ledger.slice(0, 12)) : [];

  return (
    <div class="page">
      <div class="page-inner">
        <h1>Retkipassi</h1>
        <p class="lede">Oma edistyminen. Historia säilyy, vaikka pitäisit taukoa.</p>
        <AccountProfile app={app} />
        <BackupWarning app={app} />

        <section class="level-card" aria-label="Taso">
          <div class="row">
            <div><small>Taso</small><div class="lv">{lv.level}</div></div>
            <div style={{ textAlign: "right" }}><div class="xp num">{lv.xp.toLocaleString("fi-FI")}</div><small>pistettä</small></div>
          </div>
          <div class="bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)} aria-label="Edistyminen seuraavalle tasolle">
            <i style={{ width: `${progress * 100}%` }} />
          </div>
          <small>{Math.max(0, lv.next_level_at - lv.xp)} pistettä tasolle {lv.level + 1}</small>
        </section>

        <div class="punch" aria-label="Kerätyt">
          <div><b>{stats?.small_tiles ?? 0}</b><span>pikkuruutua</span></div>
          <div><b>{stats?.large_tiles ?? 0}</b><span>suurta ruutua</span></div>
          {stats?.large_done > 0 && <div><b>{stats.large_done}</b><span>valloitettua</span></div>}
          <div><b>{stats?.trips ?? 0}</b><span>retkeä</span></div>
          <div><b>{stats?.pois ?? 0}</b><span>kohdetta</span></div>
          <div><b>{stats?.routes ?? 0}</b><span>reittiä</span></div>
          {stats?.secrets > 0 && <div><b>{stats.secrets}</b><span>salaista</span></div>}
          <div><b>{kmValue(stats?.eligible_km ?? 0).replace(" km", "")}</b><span>km lihasvoimalla</span></div>
        </div>

        <ShapesCard app={app} />

        <BigTilesCard app={app} />

        <TeamCard app={app} />
        {app.account.role === "admin" && <TeamAdmin app={app} />}

        <AdventureLine log={app.adventureLog} />

        <Fold title="Merkit" count={`${badges.filter((b) => b.unlocked).length}/${badges.length}`} open>
        <div class="badge-grid">
          {badges.map((b) => (
            <button key={b.id} class={`stamp ${b.unlocked ? "open" : ""}`} onClick={() => setOpenBadge(b)} aria-label={`${b.name}${b.unlocked ? ", avattu" : ", lukittu"}`}>
              <svg viewBox="0 0 48 48" aria-hidden="true">
                <circle cx="24" cy="24" r="21" class="ring" />
                <circle cx="24" cy="24" r="16" class="ring inner" />
                <circle cx="24" cy="24" r="3.5" class="dot" />
              </svg>
              <span>{b.name}</span>
            </button>
          ))}
        </div>
        </Fold>
        {openBadge && (
          <Sheet title={openBadge.name} onClose={() => setOpenBadge(null)}>
            <p class="hint">{openBadge.how}</p>
            <p>{openBadge.unlocked ? `Avattu ${day(openBadge.unlocked_at)}.` : "Ei vielä avattu."}</p>
            <div class="sheet-actions">
              {openBadge.trip_id && (
                <button class="btn btn-primary" onClick={() => { setOpenBadge(null); app.openTrip(openBadge.trip_id); }}>Avaa retki</button>
              )}
              <button class="btn" onClick={() => setOpenBadge(null)}>Sulje</button>
            </div>
          </Sheet>
        )}

        <WeeklyGoal app={app} />

        <YearCard app={app} />

        <Fold title="Kokoelmat" count={content ? `${content.collections.filter((c) => c.state.done).length}/${content.collections.length}` : null}>
        {content?.collections.map((c) => (
          <div class="coll" key={c.id}>
            <div class="area-head" style={{ margin: 0 }}>
              <b>{c.name}</b>
              <span class={c.state.done ? "state-done" : "s"}>{c.state.done ? "Valmis" : `${c.done_count}/${c.members.length}`}</span>
            </div>
            <div class="meter"><i style={{ width: `${(c.done_count / Math.max(1, c.members.length)) * 100}%`, background: c.state.done ? "var(--gold)" : undefined }} /></div>
          </div>
        ))}
        </Fold>

        <Fold title="Mistä pisteet tulivat" count={ledger ? ledger.length : null}>
        {ledger?.length === 0 && <div class="empty"><b>Ei vielä pisteitä</b>Jokainen piste-erä näkyy tässä perusteineen.</div>}
        {shown.map((e) => (
          <div class="ledger-row" key={e.id}>
            <div><b>{e.reason_text}</b>{e.kind !== "tile" && e.kind !== "bigtile" ? `: ${e.label}` : ""}</div>
            <div class={`amt ${e.delta < 0 ? "neg" : ""}`}>{fmtPoints(e.delta)}</div>
            <div class="why">{day(e.at)}, {e.basis.charAt(0).toLowerCase() + e.basis.slice(1)}{e.note ? `. ${e.note}` : ""}</div>
          </div>
        ))}
        {ledger && ledger.length > 12 && (
          <button class="btn btn-quiet" onClick={() => setShowAll(!showAll)}>{showAll ? "Näytä vähemmän" : `Näytä kaikki ${ledger.length}`}</button>
        )}
        </Fold>

        <UpdateCard update={app.update} />

        <Settings app={app} />
      </div>
    </div>
  );
}

/** Kuka pelaa: näkyy vain, kun pelaajia on enemmän kuin yksi. */
/** Suoritettujen seikkailujen määrä pituuksittain. Ei näy, ennen kuin ensimmäinen on tehty. */
function AdventureLine({ log }) {
  const done = log.filter((a) => a.done);
  if (!done.length) return null;
  const count = (length) => done.filter((a) => a.length === length).length;
  return (
    <p class="s adv-pass">
      Suoritettuja seikkailuja: <b>{done.length}</b> (lyhyt {count("short")}, keskipitkä {count("medium")}, pitkä {count("long")})
    </p>
  );
}

function Settings({ app }) {
  const [server, setServer] = useState(getServerSettings());
  const [status, setStatus] = useState(null);

  const save = async () => {
    const changedServer = server.baseUrl.replace(/\/+$/, "") !== getServerSettings().baseUrl.replace(/\/+$/, "");
    setServerSettings({ baseUrl: server.baseUrl.replace(/\/+$/, ""), apiKey: server.apiKey });
    if (changedServer) { location.reload(); return; }
    try {
      await app.api.health();
      await app.api.stats();
      setStatus({ ok: true, text: "Yhteys toimii." });
      app.refresh();
      app.syncNow();
    } catch (e) {
      setStatus({ ok: false, text: e.message });
    }
  };

  /** Hakee tiedoston palvelimelta ja tallentaa sen (selain: lataus, puhelin: jakovalikko). */
  const saveFile = async (fetchText, filename, mime) => {
    try {
      await saveTextFile(filename, await fetchText(), mime);
    } catch (e) {
      app.toast({ kind: "info", what: "Tiedostoa ei saatu", sub: e.message });
    }
  };
  const download = () =>
    saveFile(async () => JSON.stringify(await app.api.exportBackup()), `tilehunting-varmuuskopio-${todayIso()}.json`, "application/json");

  const upload = async (file, mode) => {
    if (!file) return;
    try {
      const data = JSON.parse(await file.text());
      const result = await app.api.importBackup(data, mode);
      app.toast({ kind: "info", what: "Varmuuskopio palautettu", sub: `${result.trips} retkeä lisätty.` });
      app.refresh();
    } catch (e) {
      app.toast({ kind: "info", what: "Palautus epäonnistui", sub: e instanceof SyntaxError ? "Tiedosto ei ole kelvollinen varmuuskopio." : e.message });
    }
  };

  return (
    <>
      <Fold title="Asetukset">
        <div class="field">
          <label for="skin">Ulkoasu</label>
          <select id="skin" value={app.skin} onChange={(e) => app.setSkin(e.currentTarget.value)}>
            {SKINS.map((s) => <option value={s.id} key={s.id}>{s.label}</option>)}
          </select>
          <p class="s">{skinById(app.skin).about}</p>
        </div>
        {themeApplies(app.skin) ? (
          <div class="field">
            <label for="theme">Vaalea vai tumma</label>
            <select id="theme" value={app.theme} onChange={(e) => app.setTheme(e.currentTarget.value)}>
              <option value="auto">Puhelimen mukaan</option>
              <option value="light">Vaalea, päivänvaloon</option>
              <option value="dark">Tumma</option>
            </select>
          </div>
        ) : (
          <p class="s">Tällä ulkoasulla on oma kiinteä väripaletti. Vaalea/tumma-valinta tulee takaisin Suunnistus-ulkoasulla.</p>
        )}
        <Preferences />
      </Fold>

      <Fold title="Palvelin (lisäasetus)">
        <p class="s">Julkaisu-APK:ssa osoite ja avain ovat jo valmiina, eikä niitä tarvitse muuttaa.</p>
        <div class="field">
          <label for="srv">Palvelimen osoite</label>
          <input id="srv" inputMode="url" autocomplete="off" placeholder="http://raspberrypi.local:8000" value={server.baseUrl} onInput={(e) => setServer({ ...server, baseUrl: e.currentTarget.value })} />
        </div>
        <div class="field">
          <label for="key">API-avain</label>
          <input id="key" type="password" autocomplete="off" placeholder="Tyhjä, jos palvelimella ei ole avainta" value={server.apiKey} onInput={(e) => setServer({ ...server, apiKey: e.currentTarget.value })} />
        </div>
        <div class="sheet-actions">
          <button class="btn btn-primary" onClick={save}>Tallenna ja testaa yhteys</button>
        </div>
        {status && <p class={status.ok ? "s" : "rec-note warn"} role="status">{status.text}</p>}
      </Fold>

      <Fold title="Varmuuskopio">
        <p class="s">Sisältää retket, ruudut, omat vahvistukset, koko pistehistorian sekä itse luodut kohteet ja reitit.</p>
        <div class="sheet-actions">
          <button class="btn" onClick={download}>Lataa varmuuskopio</button>
          <label class="btn">
            Yhdistä varmuuskopiosta
            <input type="file" accept="application/json" class="sr-only" onChange={(e) => upload(e.currentTarget.files[0], "merge")} />
          </label>
        </div>
        <p class="s">Yhdistäminen lisää vain puuttuvat retket. Saman kopion voi tuoda uudelleen ilman kaksoispisteitä.</p>
      </Fold>

      <Fold title="Vienti muihin ohjelmiin">
        <p class="s">GPX avautuu karttasovelluksissa. CSV-tiedostot avautuvat Excelissä.</p>
        <div class="sheet-actions">
          <button class="btn" onClick={() => saveFile(() => app.api.exportGpx(), `tilehunting-retket-${todayIso()}.gpx`, "application/gpx+xml")}>Kaikki retket GPX-tiedostona</button>
          <button class="btn" onClick={() => saveFile(() => app.api.exportLedgerCsv(), `tilehunting-pisteet-${todayIso()}.csv`, "text/csv")}>Pistehistoria CSV-tiedostona</button>
          <button class="btn" onClick={() => saveFile(() => app.api.exportTripsCsv(), `tilehunting-retket-${todayIso()}.csv`, "text/csv")}>Retkien yhteenveto CSV-tiedostona</button>
        </div>
      </Fold>

      <AdminSection app={app} />
    </>
  );
}

function Preferences() {
  const [prefs, setLocal] = useState(getPrefs());
  const toggle = (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setLocal(next);
    setPrefs(next);
  };
  return (
    <div class="pref-list">
      <label class="check">
        <input type="checkbox" checked={prefs.reduceMotion} onChange={() => toggle("reduceMotion")} />
        <span>Vähennä animaatioita</span>
      </label>
      <label class="check">
        <input type="checkbox" checked={prefs.vibrate} onChange={() => toggle("vibrate")} />
        <span>Värinä uusista löydöistä</span>
      </label>
    </div>
  );
}

/**
 * Ylläpito: avaimella avautuvat tekninen loki ja salaisten kohteiden hallinta. Tavallisella pelaajalla
 * näkyy vain pieni Ylläpito-painike, joten asetukset pysyvät selkeinä. Avain tarkistetaan palvelimella.
 */
function AdminSection({ app }) {
  if (!app.admin) return null;
  return <Fold title="Ylläpito">
    <p class="s">Pääkäyttäjän oikeudet kuuluvat tilillesi. Salaisia kohteita hallitaan Kohteet-sivulta.</p>
    <ContentAdminCard app={app} /><BackupStatusCard app={app} /><DiagnosticsLog />
  </Fold>;
}

export function DiagnosticsLog() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);
  const entries = open ? readLog() : [];
  return (
    <>
      <h2>Tekninen loki</h2>
      <p class="s">Sovelluksen omat tapahtumat vianetsintää varten. Loki on vain tässä puhelimessa eikä sisällä sijainteja.</p>
      {!open ? (
        <button class="btn" onClick={() => { setOpen(true); setText(formatLog()); }}>Näytä loki</button>
      ) : (
        <>
          <pre class="diag" aria-label="Lokin sisältö">{entries.length ? text : "Loki on tyhjä."}</pre>
          <div class="sheet-actions">
            <button class="btn" onClick={async () => {
              try { await navigator.clipboard.writeText(text); setCopied(true); } catch { setCopied(false); }
            }}>{copied ? "Kopioitu" : "Kopioi loki"}</button>
            <button class="btn btn-quiet" onClick={() => { clearLog(); setText(formatLog([])); }}>Tyhjennä loki</button>
          </div>
        </>
      )}
    </>
  );
}

const MONTH_LETTERS = ["T", "H", "M", "H", "T", "K", "H", "E", "S", "L", "M", "J"];

/** Vuosiyhteenveto (kohta 13.3): uudet löydöt, kilometrit ja kuukausittaiset ruudut. */
function YearCard({ app }) {
  const [year, setYear] = useState(null);
  const [data, setData] = useState(null);
  useEffect(() => {
    app.api.yearSummary(year).then(setData, () => setData(null));
  }, [year, app.dataVersion]);
  if (!data) return null;
  const peak = Math.max(1, ...data.tiles_by_month);
  return (
    <section class="year-card" aria-label={`Vuosi ${data.year}`}>
      <div class="year-head">
        <h2 style={{ margin: 0 }}>Vuosi {data.year}</h2>
        {data.years.length > 1 && (
          <select aria-label="Valitse vuosi" value={data.year} onChange={(e) => setYear(Number(e.currentTarget.value))}>
            {data.years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
      </div>
      <div class="year-figs">
        <div><b>{data.trips}</b><span>retkeä</span></div>
        <div><b>{data.eligible_km.toLocaleString("fi-FI")}</b><span>km</span></div>
        <div><b>{data.new_tiles}</b><span>uutta ruutua</span></div>
      </div>
      <div class="months" role="img" aria-label={`Uudet ruudut kuukausittain: ${data.tiles_by_month.join(", ")}`}>
        {data.tiles_by_month.map((n, i) => <i key={i} class={n ? "" : "zero"} style={{ height: `${Math.max(3, (n / peak) * 100)}%` }} />)}
      </div>
      <div class="month-labels" aria-hidden="true">{MONTH_LETTERS.map((m, i) => <span key={i}>{m}</span>)}</div>
      <p class="s" style={{ marginBottom: 0 }}>
        {data.trips === 0 ? "Ei retkiä tänä vuonna." : (
          <>
            {data.best_month && `Eniten löytöjä: ${data.best_month}. `}
            {data.favourite_mode && `Yleisin kulkutapa: ${data.favourite_mode.toLowerCase()}. `}
            {data.longest_trip && `Pisin retki: ${data.longest_trip.name}, ${data.longest_trip.km.toLocaleString("fi-FI")} km. `}
            {data.places.length > 0 && `Uudet paikat: ${data.places.join(", ")}.`}
          </>
        )}
      </p>
    </section>
  );
}

/**
 * Viikkotavoite (kohta 7). Joustava: vajaaksi jäänyt viikko ei nollaa
 * mitään, eikä putkia lasketa. 0 tarkoittaa, ettei tavoitetta ole.
 */
function WeeklyGoal({ app }) {
  const w = app.weekly;
  if (!w) return null;
  const setGoal = async (goal) => {
    try {
      app.setWeekly(await app.api.setWeeklyGoal(goal));
    } catch (e) {
      app.toast({ kind: "info", what: "Tavoitetta ei tallennettu", sub: e.message });
    }
  };
  const weeks = [...w.history].reverse(); // vanhin vasemmalla
  return (
    <section class="week-card" aria-label="Viikkotavoite">
      <div class="year-head">
        <h2 style={{ margin: 0 }}>Viikkotavoite</h2>
        <div class="stepper" role="group" aria-label="Retkiä viikossa">
          <button class="btn" aria-label="Vähemmän" disabled={w.goal <= 0} onClick={() => setGoal(w.goal - 1)}>−</button>
          <span class="num" aria-live="polite">{w.goal || "ei"}</span>
          <button class="btn" aria-label="Enemmän" disabled={w.goal >= 14} onClick={() => setGoal(w.goal + 1)}>+</button>
        </div>
      </div>
      <p class="s" style={{ margin: "6px 0 10px" }}>
        {w.goal
          ? `Tällä viikolla ${w.this_week} / ${w.goal} retkeä. Vajaa viikko ei nollaa mitään.`
          : "Aseta halutessasi retkimäärä viikolle. Putkia ei lasketa, joten tauot eivät rankaise."}
      </p>
      {w.goal > 0 && (
        <div class="week-dots" role="img" aria-label={`Kahdeksan viikkoa: ${weeks.map((x) => x.trips).join(", ")} retkeä`}>
          {weeks.map((x, i) => (
            <span key={x.week_start} class={`${x.met ? "met" : ""} ${i === weeks.length - 1 ? "now" : ""}`}>{x.trips}</span>
          ))}
        </div>
      )}
    </section>
  );
}
